import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Thumbnail, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useAppSelector } from '../../../hooks/react-redux';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { getCurrentPdfPage } from './store';
import styles from './DocumentViewer.module.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFSideBarProps {
    numPages: number | null;
    width: number;
}

const THUMBNAIL_PADDING_AND_SCROLLBAR_OFFSET = 20;

export const PDFSideBar: React.FC<PDFSideBarProps> = ({
    numPages,
    width
}) => {
    const thumbnailsRef = useRef<Map<number, HTMLDivElement> | null>(null);
    const pageArray = useMemo(() => numPages ? new Array(numPages).fill(1).map((_, index) => index + 1) : [], [numPages]);

    const getThumbnailsMap = useCallback(() => {
        if (!thumbnailsRef.current) {
            thumbnailsRef.current = new Map<number, HTMLDivElement>();
        }
        return thumbnailsRef.current!;
    }, []);

    const setThumbnailRef = useCallback((element: HTMLDivElement | null, pageNumber: number) => {
        const map = getThumbnailsMap();
        if (element) {
            map.set(pageNumber, element);
        } else {
            map.delete(pageNumber);
        }
    }, [getThumbnailsMap]);

    const currentPage = useAppSelector(getCurrentPdfPage);

    const checkThumbnailsScrolled = useCallback(() => {
        const thumbnails = getThumbnailsMap();
        const node = thumbnails.get(currentPage);
        if (node) {
            const { top, bottom } = node.getBoundingClientRect();
            const isVisible = (top >= 0) && (bottom <= window.innerHeight);
            if (!isVisible) {
                node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }
        }
    }, [getThumbnailsMap, currentPage]);

    useEffect(() => {
        const thumbnailTimer = setTimeout(() => checkThumbnailsScrolled(), 1000);
        return () => {
            clearTimeout(thumbnailTimer);
        };
    }, [checkThumbnailsScrolled]);

    return (
        <div className={styles.sidebarWrapper} style={{ width: '10%' }}>
            <Scrollable>
                {pageArray.map(page => (
                    <div key={`thumbnail_${page}`} className={styles.thumbnailWrapper}>
                        <div className={styles.thumbnailPageNumber} style={{ left: 3, top: 3 }}>{page}</div>
                        <div className={classnames(styles.thumbnail, { [styles.selectedThumbnail]: page === currentPage })}>
                            <Thumbnail
                                pageNumber={page}
                                loading=''
                                width={width * 0.1 - THUMBNAIL_PADDING_AND_SCROLLBAR_OFFSET}
                                inputRef={element => setThumbnailRef(element, page)}
                            />
                        </div>
                    </div>
                ))}
            </Scrollable>
        </div>
    );
};
